/**
 * Utility functions for optimization analysis
 */

/**
 * Safe number conversion helper to handle NaN values
 */
export const safeNumber = (value: any, defaultValue: number = 0): number => {
  if (value === null || value === undefined) return defaultValue;
  const num = Number(value);
  return isNaN(num) || !isFinite(num) ? defaultValue : num;
};

/**
 * Format number with thousands separator
 */
export const formatNumber = (value: number, decimals: number = 0): string => {
  const safeValue = safeNumber(value, 0);
  if (!isFinite(safeValue)) return '0';
  return safeValue.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Format currency values
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Format percentage values
 */
export const formatPercentage = (value: number, decimals: number = 1): string => {
  return `${(value * 100).toFixed(decimals)}%`;
};

/**
 * Format power values (kW, MW)
 */
export const formatPower = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)} MW`;
  }
  return `${value.toFixed(0)} kW`;
};

/**
 * Format energy values (kWh, MWh)
 */
export const formatEnergy = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)} MWh`;
  }
  return `${value.toFixed(0)} kWh`;
};

/**
 * Get risk level color classes
 */
export const getRiskLevelColor = (level: 'low' | 'medium' | 'high'): string => {
  switch (level) {
    case 'low':
      return 'text-green-600 bg-green-50';
    case 'medium':
      return 'text-yellow-600 bg-yellow-50';
    case 'high':
      return 'text-red-600 bg-red-50';
    default:
      return 'text-gray-600 bg-gray-50';
  }
};

/**
 * Get metric color based on value and thresholds
 */
export const getMetricColor = (value: number, goodThreshold: number, isHigherBetter: boolean = true): string => {
  if (isHigherBetter) {
    return value >= goodThreshold ? 'text-green-600' : 'text-yellow-600';
  } else {
    return value <= goodThreshold ? 'text-green-600' : 'text-yellow-600';
  }
};